import { Measure, NoteEvent, HarmonyEvent, RegionSpec, barStart, barEnd } from './piece';
import { parseKey } from './key';
import { midiOf, parsePitch, pcNumber, pitchName } from './pitch';

/** Notes for a list of spelled pitch names at one time. */
function event(name: string, onset: number, dur: number, bar: number, line: number, altered = false): NoteEvent {
  const p = parsePitch(name);
  return { bar, onset, dur, midi: midiOf(p), name: pitchName(p), line, altered };
}

/**
 * The figuration of Bach's Prelude in C, BWV 846 (bars 1–32): per half bar, the two
 * lowest notes are held and the upper three are broken twice in sixteenths.
 */
export function preludeFiguration(pitches: string[], start: number, bar: number, altered = false): NoteEvent[] {
  if (pitches.length !== 5) throw new Error('Prelude figuration needs five pitches');
  const sorted = [...pitches].sort((a, b) => midiOf(parsePitch(a)) - midiOf(parsePitch(b)));
  const out: NoteEvent[] = [];
  for (let half = 0; half < 2; half++) {
    const t = start + half * 2;
    out.push(event(sorted[0], t, 2, bar, 0, altered));
    out.push(event(sorted[1], t + 0.25, 1.75, bar, 1, altered));
    for (let k = 0; k < 6; k++) {
      const idx = 2 + (k % 3);
      out.push(event(sorted[idx], t + 0.5 + 0.25 * k, 0.25, bar, idx, altered));
    }
  }
  return out;
}

/** A block chord (all pitches struck together). */
export function blockChord(pitches: string[], start: number, dur: number, bar: number): NoteEvent[] {
  const sorted = [...pitches].sort((a, b) => midiOf(parsePitch(a)) - midiOf(parsePitch(b)));
  return sorted.map((name, i) => event(name, start, dur, bar, i));
}

/** Implied contrapuntal lines: rank of each pitch within its bar (0 = lowest). */
export function assignLines(notes: NoteEvent[], maxLine = 4): NoteEvent[] {
  const byBar = new Map<number, number[]>();
  for (const n of notes) {
    const list = byBar.get(n.bar) ?? [];
    if (!list.includes(n.midi)) list.push(n.midi);
    byBar.set(n.bar, list);
  }
  byBar.forEach((list) => list.sort((a, b) => a - b));
  return notes.map((n) => ({ ...n, line: Math.min(maxLine, byBar.get(n.bar)!.indexOf(n.midi)) }));
}

export type Layer = 'surface' | 'chords' | 'frame' | 'ground' | 'plan';

function strikes(start: number, end: number, every = 2): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let t = start; t < end - 1e-9; t += every) out.push([t, Math.min(every, end - t)]);
  return out;
}

function distinctSounding(ev: HarmonyEvent): NoteEvent[] {
  const seen = new Map<number, NoteEvent>();
  for (const n of ev.sounding) if (!seen.has(n.midi)) seen.set(n.midi, n);
  return [...seen.values()].sort((a, b) => a.midi - b.midi);
}

/**
 * Strip the surface away, layer by layer, deterministically:
 * surface (as written) → chords (block harmony) → frame (bass + top) → ground (bass) → plan (key centres).
 */
export function applyLayer(layer: Layer, notes: NoteEvent[], events: HarmonyEvent[], measures: Measure[], regions: RegionSpec[]): NoteEvent[] {
  if (layer === 'surface') return notes;
  const out: NoteEvent[] = [];
  if (layer === 'plan') {
    for (const r of regions) {
      const k = parseKey(r.key);
      for (let bar = r.from; bar <= r.to; bar++) {
        if (!measures.some((m) => m.bar === bar)) continue;
        const s = barStart(measures, bar);
        const e = barEnd(measures, bar);
        // Tonic in the octave C2–B2, with its fifth above, restruck each bar.
        const tonicMidi = 36 + pcNumber(k.tonic);
        out.push({ bar, onset: s, dur: e - s, midi: tonicMidi, name: '', line: 0 });
        out.push({ bar, onset: s, dur: e - s, midi: tonicMidi + 7, name: '', line: 1 });
        out.push({ bar, onset: s, dur: e - s, midi: tonicMidi + 12 + (k.mode === 'minor' ? 3 : 4), name: '', line: 2 });
      }
    }
    return out;
  }
  for (const ev of events) {
    const pitches = distinctSounding(ev);
    if (!pitches.length) continue;
    const chosen = layer === 'chords' ? pitches : layer === 'frame' ? [pitches[0], pitches[pitches.length - 1]] : [pitches[0]];
    for (const [t, d] of strikes(ev.start, ev.end)) {
      chosen.forEach((n, i) => out.push({ ...n, onset: t, dur: d, line: layer === 'frame' && i === 1 ? 4 : i === 0 ? 0 : i }));
    }
  }
  return out;
}
